import { useCallback, useRef, useState } from 'react'
import {
  Box,
  FormControl,
  FormHelperText,
  Input,
  Spinner,
  Text,
} from '@chakra-ui/react'
import { useThemeColors } from '@/hooks/useThemeColors'
import { TicketService } from '@/lib/services/ticket'
import { ticketToFormValues } from '@/lib/utils/parentTicketFormPayload'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import { useParentTicketStore } from '@/store/parentTicket'
import type { ClientOption } from '@/types/ticket'

type Props = {
  ticketId: string
  clientCode?: string
  isReadOnly?: boolean
  onSaved?: (store: ClientOption) => void | Promise<void>
}

export default function TicketStorePicker({
  ticketId,
  clientCode,
  isReadOnly = false,
  onSaved,
}: Props) {
  const colors = useThemeColors()
  const { showError, showSuccess } = useToastNotification()
  const getTicketById = useParentTicketStore((s) => s.getTicketById)

  const [searchTerm, setSearchTerm] = useState('')
  const [results, setResults] = useState<ClientOption[]>([])
  const [searching, setSearching] = useState(false)
  const [saving, setSaving] = useState(false)
  const [selected, setSelected] = useState<ClientOption | null>(null)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const requestIdRef = useRef(0)

  const inputBaseProps = {
    bg: 'white',
    borderColor: colors.inputBorder ?? 'gray.200',
    borderRadius: 'lg',
    size: 'sm' as const,
    _hover: { borderColor: colors.inputborderHover ?? colors.inputBorder ?? 'gray.300' },
    _focusVisible: {
      borderColor: colors.primary ?? 'blue.500',
      boxShadow: `0 0 0 1px ${colors.primary ?? '#3182ce'}`,
    },
  }

  const searchStores = useCallback(
    async (term: string) => {
      if (term.length < 3) {
        setResults([])
        return
      }
      const requestId = ++requestIdRef.current
      setSearching(true)
      try {
        const response = await TicketService.searchStoreOptions(term, clientCode)
        if (requestId !== requestIdRef.current) return
        setResults(response.success && response.data?.length ? response.data : [])
      } catch {
        if (requestId === requestIdRef.current) setResults([])
      } finally {
        if (requestId === requestIdRef.current) setSearching(false)
      }
    },
    [clientCode],
  )

  const persistStore = async (store: ClientOption) => {
    const ticket = getTicketById(ticketId) as ReturnType<typeof getTicketById> & Record<string, unknown>
    const base = ticket ? ticketToFormValues(ticket, ticketId) : null
    if (!base) {
      showError('Could not load ticket data for saving store')
      return
    }
    setSaving(true)
    try {
      const res = await TicketService.updateParentTicket(ticketId, {
        ...base,
        storeId: store.storeId,
        storeCode: store.storeCode,
      })
      if (!res.success) throw new Error(res.message || 'Failed to save store')
      setSelected(store)
      setSearchTerm(`[${store.storeCode}] - ${store.storeName}`)
      setResults([])
      showSuccess('Store linked to ticket')
      await onSaved?.(store)
    } catch (e: unknown) {
      showError(e instanceof Error ? e.message : 'Failed to save store')
    } finally {
      setSaving(false)
    }
  }

  const handleSelect = (store: ClientOption) => {
    if (isReadOnly) return
    void persistStore(store)
  }

  if (selected) {
    return (
      <Box mb={4} p={3} bg={colors.sectionBg ?? 'gray.50'} borderRadius="md" borderWidth="1px" borderColor={colors.borderLight}>
        <Text fontSize="xs" fontWeight="700" color={colors.textMuted} mb={1}>
          LINKED STORE
        </Text>
        <Text fontSize="sm" fontWeight="600" color={colors.text}>
          [{selected.storeCode}] {selected.storeName}
        </Text>
        {(selected.branchAddress ?? selected.locationAddress) && (
          <Text fontSize="xs" color={colors.textSecondary} mt={1}>
            {selected.branchAddress ?? selected.locationAddress}
          </Text>
        )}
        {selected.latitude != null && selected.longitude != null && (
          <Box mt={2} borderRadius="md" overflow="hidden" border="1px solid" borderColor={colors.borderLight}>
            <iframe
              title="Store location"
              src={`https://www.openstreetmap.org/export/embed.html?bbox=${selected.longitude - 0.01}%2C${selected.latitude - 0.01}%2C${selected.longitude + 0.01}%2C${selected.latitude + 0.01}&layer=mapnik&marker=${selected.latitude}%2C${selected.longitude}`}
              width="100%"
              height="140"
              style={{ border: 0 }}
              loading="lazy"
            />
          </Box>
        )}
      </Box>
    )
  }

  return (
    <Box mb={4} p={3} bg="orange.50" borderRadius="md" borderWidth="1px" borderColor="orange.200">
      <Text fontSize="sm" fontWeight="600" color={colors.text} mb={2}>
        Store required for FE scheduling
      </Text>
      <Text fontSize="xs" color={colors.textSecondary} mb={3}>
        Search and select the store before recommending field engineers.
      </Text>
      <FormControl>
        <Box position="relative">
          <Input
            value={searchTerm}
            onChange={(e) => {
              const value = e.target.value
              setSearchTerm(value)
              if (timeoutRef.current) clearTimeout(timeoutRef.current)
              timeoutRef.current = setTimeout(() => searchStores(value), 300)
            }}
            placeholder="Search stores (min 3 characters)..."
            isDisabled={isReadOnly || saving}
            {...inputBaseProps}
          />
          {searching || saving ? (
            <Box position="absolute" right={3} top="50%" transform="translateY(-50%)">
              <Spinner size="sm" color={colors.primary} />
            </Box>
          ) : null}
          {results.length > 0 && !searching ? (
            <Box
              position="absolute"
              zIndex={10}
              w="100%"
              mt={1}
              bg="white"
              border="1px solid"
              borderColor={colors.borderLight}
              borderRadius="md"
              boxShadow="md"
              maxH="240px"
              overflowY="auto"
            >
              {results.map((store) => (
                <Box
                  key={store.storeCode}
                  px={4}
                  py={2}
                  cursor="pointer"
                  _hover={{ bg: 'gray.100' }}
                  onClick={() => handleSelect(store)}
                  borderBottom="1px solid"
                  borderColor={colors.borderLight}
                  _last={{ borderBottom: 'none' }}
                >
                  <Text fontWeight="600" fontSize="sm" color={colors.text}>
                    [{store.storeCode}] - {store.storeName}
                  </Text>
                  {store.businessUnitName ? (
                    <Text fontSize="xs" color={colors.textSecondary} mt={1}>
                      {store.businessUnitName}
                    </Text>
                  ) : null}
                </Box>
              ))}
            </Box>
          ) : null}
        </Box>
        <FormHelperText fontSize="xs" color={colors.textMuted} mt={1}>
          Store is saved to the ticket when selected.
        </FormHelperText>
      </FormControl>
    </Box>
  )
}
